// Documentation: https://github.com/zeit/next.js/blob/canary/examples/with-scroll-restoration/lib/scroll-restoration.ts
import { RefObject, useCallback, useEffect, useRef } from 'react';

type ScrollToBaseOptions = Pick<ScrollToOptions, 'behavior'> & {
	offset?: number;
	delay?: number;
};

type ScrollToSectionOptions = ScrollToBaseOptions & {
	sectionRef: Element | null;
};

type HeaderOffsetOptions = {
	headerSelector?: string;
	headerOffset?: number;
};

type GetScrollTargetWithHeaderOptions = HeaderOffsetOptions & {
	bodyRef: RefObject<HTMLElement | null>;
};

type ScrollTopIfNeededOptions = ScrollToBaseOptions &
	GetScrollTargetWithHeaderOptions;

const MAIN_HEADER_SELECTOR = '[class*="header-navigation_header-navigation"]';

const isBrowser = typeof window !== 'undefined';

export const useScrollTo = () => {
	const timeoutIdRef = useRef<number | null>(null);

	const clearTimeoutIfNeeded = useCallback(() => {
		if (timeoutIdRef.current !== null) {
			clearTimeout(timeoutIdRef.current);
			timeoutIdRef.current = null;
		}
	}, []);

	useEffect(() => {
		return () => {
			clearTimeoutIfNeeded();
		};
	}, [clearTimeoutIfNeeded]);

	const scheduleScroll = useCallback(
		(scrollFn: () => void, delay: number) => {
			if (!delay) {
				scrollFn();
				return;
			}

			clearTimeoutIfNeeded();

			timeoutIdRef.current = window.setTimeout(() => {
				scrollFn();
				timeoutIdRef.current = null;
			}, delay);
		},
		[clearTimeoutIfNeeded]
	);

	const scrollToTop = useCallback(
		({
			offset = 0,
			behavior = 'smooth',
			delay = 0,
		}: ScrollToBaseOptions = {}) => {
			if (!isBrowser) return;

			scheduleScroll(() => {
				window.scrollTo({ top: offset, behavior });
			}, delay);
		},
		[scheduleScroll]
	);

	const scrollToSection = useCallback(
		({
			sectionRef,
			offset = 20,
			behavior = 'smooth',
			delay = 0,
		}: ScrollToSectionOptions) => {
			if (!isBrowser || !sectionRef) return;

			scheduleScroll(() => {
				const sectionTopPosition =
					sectionRef.getBoundingClientRect().top;
				const sectionOffset =
					sectionTopPosition + window.scrollY - offset;

				window.scrollTo({ top: sectionOffset, behavior });
			}, delay);
		},
		[scheduleScroll]
	);

	const getScrollTargetWithHeader = useCallback(
		({
			bodyRef,
			headerSelector = MAIN_HEADER_SELECTOR,
			headerOffset = 16,
		}: GetScrollTargetWithHeaderOptions) => {
			if (!isBrowser) return null;

			const element = bodyRef.current;
			if (!element) return null;

			const header = document.querySelector(headerSelector);
			const headerHeight = header
				? header.getBoundingClientRect().height
				: 0;
			const rect = element.getBoundingClientRect();
			const isHiddenByHeader = rect.top < headerHeight;
			const isAboveViewport = rect.top < 0;

			if (isHiddenByHeader || isAboveViewport) {
				return (
					window.pageYOffset + rect.top - headerHeight - headerOffset
				);
			}

			return null;
		},
		[]
	);

	const scrollTopIfNeeded = useCallback(
		({
			bodyRef,
			headerSelector = MAIN_HEADER_SELECTOR,
			headerOffset = 16,
			behavior = 'smooth',
			delay = 0,
		}: ScrollTopIfNeededOptions) => {
			if (!isBrowser) return;

			requestAnimationFrame(() => {
				const scrollTarget = getScrollTargetWithHeader({
					bodyRef,
					headerSelector,
					headerOffset,
				});

				if (scrollTarget === null) return;

				scrollToTop({
					offset: scrollTarget,
					behavior,
					delay,
				});
			});
		},
		[getScrollTargetWithHeader, scrollToTop]
	);

	return {
		scrollToTop,
		scrollToSection,
		getScrollTargetWithHeader,
		scrollTopIfNeeded,
	} as const;
};

export default useScrollTo;
